import { resolveClaudeBinary } from "../harness/pty/spawn-args.js";
import { loadPtySpawn, type PtyProcess, type PtySpawn } from "../harness/pty/pty-support.js";

const PROBE_TIMEOUT_MS = 30_000;
// Give the TUI time to boot before typing `/usage`, then keep retrying until the
// panel renders (first boot on a cold pod can be slow).
const FIRST_SEND_MS = 5_000;
const RESEND_INTERVAL_MS = 4_000;
const MAX_SENDS = 5;
// Once the gauge panel starts drawing, let it finish painting before capturing.
const RENDER_SETTLE_MS = 900;

interface ProbeTiming {
  firstSendMs: number;
  resendIntervalMs: number;
  settleMs: number;
}

/**
 * Env for the probe child. CLAUDE_CODE_OAUTH_TOKEN and ANTHROPIC_API_KEY are
 * STRIPPED: with either set, the CLI authenticates from the env var and `/usage`
 * has no subscription gauges to report. Without them the CLI falls back to the
 * synthesized `~/.claude/.credentials.json` — the same auth the interactive TUI
 * uses, where `/usage` renders the session/weekly gauges.
 */
export function buildProbeEnv(env: NodeJS.ProcessEnv = process.env): Record<string, string> {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(env)) {
    if (typeof value === "string") clean[key] = value;
  }
  delete clean.CLAUDE_CODE_OAUTH_TOKEN;
  delete clean.ANTHROPIC_API_KEY;
  return clean;
}

/** The gauge panel has started rendering once the session label + a percent appear. */
function panelRendering(buf: string): boolean {
  return /Current session/i.test(buf) && /%/.test(buf);
}

/**
 * One `/usage` probe run: drives an interactive `claude` under a PTY, types
 * `/usage`, and captures the rendered panel. Encapsulated so the timers, resend
 * loop, and single-resolve guard stay tidy.
 */
class UsageProbeRun {
  private buf = "";
  private settled = false;
  private sends = 0;
  private child: PtyProcess | null = null;
  private hardTimer: ReturnType<typeof setTimeout> | null = null;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly resolve: (out: string) => void,
    private readonly timing: ProbeTiming,
  ) {}

  start(child: PtyProcess, timeoutMs: number): void {
    this.child = child;
    child.onData((data) => this.onData(data));
    child.onExit(() => this.finishBestEffort());
    this.hardTimer = setTimeout(() => this.finishBestEffort(), timeoutMs);
    this.hardTimer.unref?.();
    const first = setTimeout(() => this.trySend(), this.timing.firstSendMs);
    first.unref?.();
    this.resendTimer = setInterval(() => this.trySend(), this.timing.resendIntervalMs);
    this.resendTimer.unref?.();
  }

  private trySend(): void {
    if (this.settled || this.sends >= MAX_SENDS) return;
    this.sends += 1;
    try {
      this.child?.write("/usage\r");
    } catch {
      /* pty gone — the hard timeout will resolve */
    }
  }

  private onData(chunk: string): void {
    this.buf += chunk;
    if (this.settleTimer || !panelRendering(this.buf)) return;
    // Panel is painting — capture once it settles.
    this.settleTimer = setTimeout(() => this.finish(this.buf), this.timing.settleMs);
  }

  private finishBestEffort(): void {
    this.finish(panelRendering(this.buf) ? this.buf : "");
  }

  private finish(out: string): void {
    if (this.settled) return;
    this.settled = true;
    if (this.hardTimer) clearTimeout(this.hardTimer);
    if (this.resendTimer) clearInterval(this.resendTimer);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    try {
      this.child?.kill();
    } catch {
      /* already gone */
    }
    this.resolve(out);
  }
}

export interface UsageProbeDeps {
  /** Injectable for tests; production resolves node-pty via loadPtySpawn(). */
  spawn?: PtySpawn;
  binary?: string;
  cwd?: string;
  timeoutMs?: number;
  firstSendMs?: number;
  resendIntervalMs?: number;
  settleMs?: number;
}

/**
 * Drive `claude` under a PTY, type `/usage`, and resolve the captured panel text
 * (raw, ANSI included — `parseUsageGauges` normalizes it).
 *
 * Why a PTY and not `claude -p "/usage"`: as of CLI 2.x, print mode emits only
 * the "what's contributing to your limits" insights — the session/weekly
 * utilization gauges render ONLY in the interactive `/usage` panel. Print mode
 * therefore never yields gauges, which is why the sampler produced nothing.
 *
 * Best-effort: resolves "" on spawn error, missing node-pty, or timeout without
 * a rendered panel — never rejects.
 */
export async function runUsageProbe(deps: UsageProbeDeps = {}): Promise<string> {
  let spawn = deps.spawn;
  if (!spawn) {
    try {
      spawn = await loadPtySpawn();
    } catch {
      return "";
    }
  }
  const binary = deps.binary ?? resolveClaudeBinary();
  const cwd = deps.cwd ?? process.cwd();
  const timeoutMs = deps.timeoutMs ?? PROBE_TIMEOUT_MS;
  const timing: ProbeTiming = {
    firstSendMs: deps.firstSendMs ?? FIRST_SEND_MS,
    resendIntervalMs: deps.resendIntervalMs ?? RESEND_INTERVAL_MS,
    settleMs: deps.settleMs ?? RENDER_SETTLE_MS,
  };

  return new Promise<string>((resolve) => {
    let child: PtyProcess;
    try {
      child = spawn(binary, [], {
        name: "xterm-256color",
        cols: 120,
        rows: 45,
        cwd,
        env: buildProbeEnv(),
      });
    } catch {
      resolve("");
      return;
    }
    new UsageProbeRun(resolve, timing).start(child, timeoutMs);
  });
}
